import { DataTable } from '@app/components/data-table/data-table';
import { DataTablePagination } from '@app/components/data-table/data-table-pagination';
import { buildItemScopeSearch } from '@app/purchased-goods/components/item-detail/use-item-scope';
import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import { useAnalytics } from '@app/services/analytics/AnalyticsContext';
import {
  InsightsGroupByItem,
  InsightsGroupByItemsPage,
} from '@shared/api/types';
import {
  SortingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { UNCATEGORISED_SLUG } from '@app/purchased-goods/components/taxonomy-filter-nodes';
import { useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import {
  INSIGHTS_TAXONOMY_TIERS,
  InsightsDimension,
  InsightsTaxonomyTier,
  useInsightsExplore,
  useInsightsFilters,
  useInsightsPeriodRange,
  useInsightsTableState,
  useInsightsTaxonomy,
} from '../hooks/use-insights-filters';
import {
  PACKAGING_CATEGORY_SLUG,
  getInsightsBreakdownColumns,
} from './insights-breakdown-columns';
import { insightsRowId } from './insights-grouped-charts';

const isTaxonomyTier = (
  dimension: InsightsDimension
): dimension is InsightsTaxonomyTier =>
  (INSIGHTS_TAXONOMY_TIERS as readonly string[]).includes(dimension);

type Props = {
  page?: InsightsGroupByItemsPage | null;
  dimension: InsightsDimension;
  /** Column-1 heading, e.g. "Site" or "Supplier". */
  dimensionLabel: string;
  /** Without the cost feature the spend columns stay visible but locked. */
  hasCostFeature: boolean;
  currency?: string | null;
  isLoading?: boolean;
  /** Fires with the hovered row's id on enter, and null on leave. */
  onRowHover?: (id: string | null) => void;
};

/**
 * The breakdown comparison table for the active grouping — a server-paginated,
 * server-sorted page of groups. Clicking a row drills one level down (the value
 * pins as a filter); item rows open the purchased-good detail page in a new
 * tab. The uncategorised bucket has no tiers beneath it, so it drills straight
 * to its items. Rows without an identifier don't drill.
 */
export function InsightsBreakdownTable({
  page,
  dimension,
  dimensionLabel,
  hasCostFeature,
  currency,
  isLoading,
  onRowHover,
}: Props) {
  const [, setExplore] = useInsightsExplore();
  const [filters, setFilters] = useInsightsFilters();
  const { values: taxonomy, select: selectTaxonomy } = useInsightsTaxonomy();
  const [range] = useInsightsPeriodRange();
  const [tableState, setTableState] = useInsightsTableState();
  const { track } = useAnalytics();
  const navigate = useNavigate();

  const rows = useMemo(() => [...(page?.results ?? [])], [page?.results]);

  const maxEmissions = useMemo(
    () => Math.max(0, ...rows.map((row) => row.emissionsTonnes ?? 0)),
    [rows]
  );
  const maxSpend = useMemo(
    () =>
      Math.max(
        0,
        ...rows.map((row) => (row.spend != null ? Number(row.spend) : 0))
      ),
    [rows]
  );

  const columns = useMemo(
    () =>
      getInsightsBreakdownColumns({
        dimensionLabel,
        dimension,
        packagingScope: taxonomy.productCategory === PACKAGING_CATEGORY_SLUG,
        maxEmissions,
        maxSpend,
        hasCostFeature,
        currency,
      }),
    [
      dimensionLabel,
      dimension,
      taxonomy.productCategory,
      maxEmissions,
      maxSpend,
      hasCostFeature,
      currency,
    ]
  );

  // Mirror the URL sort param (e.g. "-emissions") into tanstack sorting state.
  const sorting: SortingState = useMemo(() => {
    if (!tableState.sort) return [];
    const desc = tableState.sort.startsWith('-');
    return [{ id: tableState.sort.replace(/^-/, ''), desc }];
  }, [tableState.sort]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: page ? Math.ceil(page.count / tableState.pageSize) : 0,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater({
              pageIndex: tableState.page - 1,
              pageSize: tableState.pageSize,
            })
          : updater;
      setTableState({
        page: next.pageIndex + 1 === 1 ? null : next.pageIndex + 1,
        pageSize: next.pageSize === 10 ? null : next.pageSize,
      });
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const sort = next[0];
      setTableState({
        sort: sort ? `${sort.desc ? '-' : ''}${sort.id}` : null,
        page: null,
      });
      if (sort) {
        track('Sorted PG Insights Breakdown', {
          sort_by: sort.id,
          sort_direction: sort.desc ? 'desc' : 'asc',
        });
      }
    },
    state: {
      sorting,
      pagination: {
        pageIndex: tableState.page - 1,
        pageSize: tableState.pageSize,
      },
    },
  });

  // A row drills on its own identifier — uuid for site, supplier and item,
  // slug for the taxonomy tiers. A row carrying neither is a dead end and
  // shouldn't invite a click.
  const canDrill = (row: InsightsGroupByItem) =>
    isTaxonomyTier(dimension) ? !!row.slug : !!row.uuid;

  const drill = (rowIndex: number) => {
    const row = rows[rowIndex];
    if (!row) return;

    const trackDrill = (toDimension: string) =>
      track('Drilled Into PG Insights Row', {
        from_dimension: dimension,
        value_name: row.name,
        to_dimension: toDimension,
      });

    if (dimension === 'item') {
      if (!row.uuid) return;
      const path = generatePath(
        row.itemType === 'packaging'
          ? purchasedGoodsRoutes.PURCHASED_PACKAGING_ITEM
          : purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM,
        { item_uuid: row.uuid }
      );
      // An incomplete range means "backend default window" — carry the period
      // over only when both boundaries are explicit.
      const hasRange = Boolean(range.periodStart && range.periodEnd);
      const scopeSearch = buildItemScopeSearch({
        periodStart: hasRange ? range.periodStart : null,
        periodEnd: hasRange ? range.periodEnd : null,
        sites: filters.site,
      });
      trackDrill('item_detail');
      navigate(path + scopeSearch);
      return;
    }

    if (isTaxonomyTier(dimension)) {
      if (!row.slug) return;
      selectTaxonomy(dimension, row.slug);
      // The tier below a base product, or the uncategorised bucket at any
      // tier, is the goods themselves.
      if (dimension === 'productBase' || row.slug === UNCATEGORISED_SLUG) {
        setExplore('item');
        trackDrill('item');
      } else {
        trackDrill(
          INSIGHTS_TAXONOMY_TIERS[
            INSIGHTS_TAXONOMY_TIERS.indexOf(dimension) + 1
          ]
        );
      }
    } else if ((dimension === 'site' || dimension === 'supplier') && row.uuid) {
      setFilters({
        [dimension]: [...new Set([...filters[dimension], row.uuid])],
      });
      setExplore('category');
      trackDrill('productCategory');
    }
    setTableState({ page: null, sort: null });
  };

  return (
    <div className="flex flex-col gap-1">
      <DataTable
        table={table}
        className="bg-gray-00 text-left"
        isLoading={isLoading}
        pagination={false}
        rowClassName={(row) =>
          canDrill(row.original) ? 'hover:bg-gray-50' : 'cursor-default'
        }
        onRowClick={(row) => {
          if (row && canDrill(row.original)) drill(row.index);
        }}
        onRowMouseEnter={(row) => onRowHover?.(insightsRowId(row.original))}
        onRowMouseLeave={() => onRowHover?.(null)}
      />
      <DataTablePagination table={table} pageSizeOptions={[10, 20, 50, 100]} />
    </div>
  );
}
